// useFeatureGate — CONTRATO + FÁBRICA del gate de una feature del rollout.
//
// Problema que resuelve: la elegibilidad REAL de una feature no es solo la asignación del
// rollout (variante/invite_surface). Cada feature suma condiciones propias — compatibilidad
// del dispositivo (impresion-ble), módulo contratado (menu-v2 en el navbar), plataforma,
// permisos… Si cada superficie (navbar, sección, coachmark, página) re-deriva ese cruce por
// su cuenta, a la tercera feature hay inconsistencias garantizadas ("el navbar invita pero
// la sección no aparece").
//
// Diseño: cada feature define su gate UNA sola vez con `makeFeatureGateHook` y TODAS las
// superficies consumen el hook resultante. Las condiciones extra viajan como un hook
// (`useInvitable`) porque suelen necesitar React (useModules, contexto) — un registro
// dinámico de funciones no podría usarlas sin romper las reglas de hooks. Un hook POR
// feature implementando el MISMO contrato da type-safety, tree-shaking y cero lookup mágico.
//
// Reparto de responsabilidades (importante para no sobre-gatear):
//   - `inviteEligible`/`showInvite`: el cruce COMPLETO (rollout + condiciones extra). Es lo
//     que consumen etiquetas "Nuevo", dots y coachmarks — nunca invitar donde no se puede usar.
//   - `offered`: SOLO flag de build + variante (treatment/GA). Las condiciones de RUNTIME del
//     render (p.ej. "este navegador soporta BT") se quedan en la superficie, que decide si
//     muestra la funcionalidad o un aviso explicativo — ese matiz es de producto, no del gate.
//   - `versionControls`: las ACCIONES de gestión de versión (volver / quedarme), o `null` si la
//     feature ya no es gestionable. Ver el bloque "GESTIÓN DE VERSIÓN" más abajo.

import { useMemo } from 'react';
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { isFeatureBuildEnabled } from '@utils/featureFlags';
import type { FeatureKey } from '@utils/featureRollout/constants';

// ─── GESTIÓN DE VERSIÓN ───────────────────────────────────────────────────────────────────
//
// Problema que cierra (bug de CLASE, no de una feature): cuando una feature GRADÚA
// (`lifecycle_stage='ga'`) el backend responde `can_revert:false` / `ga_forced:true` y re-fuerza
// treatment — ya no hay vuelta atrás. Cada superficie que ofrecía "volver / quedarme" derivaba
// ESE gate a mano y una lo olvidó: `impresion-ble` graduó el 24-jun y siguió ofreciendo "Quitar la
// impresión Bluetooth" ~2 meses a 435 comercios. El revert no pegaba, pero el handler igual
// ejecutaba el reset local y el equipo perdía impresora emparejada, calibración y logo (14
// reversiones registradas post-graduación).
//
// Diseño — el estado inválido es IRREPRESENTABLE: en vez de un booleano `canManageVersion` (que
// también se puede olvidar de consultar), el contrato entrega las ACCIONES o `null`. No existe
// forma de llamar `revert()` sin haber pasado por el gate: si la feature no es gestionable no hay
// objeto del cual sacar la función. La marca de fábrica (símbolo privado del módulo) impide
// además fabricar un `FeatureVersionControls` a mano y saltarse `deriveVersionControls`.
//
// El ROL (`useEffectiveRoot`) NO entra aquí a propósito: las superficies NO lo tratan igual —
// impresion-ble y agent-chat OCULTAN el control al no-root, mientras menu-v2 (chip y card) SÍ lo
// renderiza y responde con un aviso suave o con la vista previa personal. Plegarlo al contrato
// obligaría a una de las dos conductas y cambiaría el comportamiento de la otra. El rol se queda
// donde la decisión de producto es distinta por superficie; el gate del BACKEND (lo que aquí es
// universal y olvidable) se queda en el contrato.

// Marca de fábrica: el símbolo es privado de este módulo, así que la ÚNICA manera de obtener un
// `FeatureVersionControls` es `deriveVersionControls` — que aplica el gate.
declare const versionControlsBrand: unique symbol;

export interface FeatureVersionControls {
  /** Marca de fábrica (no tiene valor en runtime): garantiza que el objeto pasó por el gate. */
  readonly [versionControlsBrand]: true;
  /** Volver a la versión anterior (rechazo definitivo). Default de trigger: 'revert_commit'. */
  revert: (trigger?: string) => void;
  /** Quedarse con la versión nueva (adopción explícita). Default de trigger: 'kept'. */
  keepCurrent: (trigger?: string) => void;
}

// Estado del que se derivan las acciones: el subconjunto de `useFeatureVersion` que gobierna la
// reversibilidad. Se acepta como parámetro (y no solo como hook) para que las superficies
// PRESENTACIONALES puedan recibir el resultado ya derivado por su host.
export interface FeatureVersionControlsSource {
  canRevert: boolean;
  gaForced: boolean;
  revert: (trigger?: string) => void;
  keepCurrent: (trigger?: string) => void;
}

// Único constructor de `FeatureVersionControls`. Devuelve `null` cuando la feature ya no es
// gestionable: graduada a GA (el backend re-fuerza treatment) o sin reversibilidad autorizada.
export function deriveVersionControls(source: FeatureVersionControlsSource): FeatureVersionControls | null {
  // Feature GRADUADA: no hay salida que ofrecer y el handler local sí haría daño.
  if (source.gaForced) return null;
  // El backend gobierna la reversibilidad (can_revert); sin ella tampoco se ofrece.
  if (!source.canRevert) return null;
  return { revert: source.revert, keepCurrent: source.keepCurrent } as FeatureVersionControls;
}

// Acciones de gestión de versión de una feature, o `null` si ya no es gestionable. Lo consumen
// las superficies que ofrecen "volver a la anterior" / "quedarme con la nueva".
export function useFeatureVersionControls(featureKey: FeatureKey): FeatureVersionControls | null {
  const { canRevert, gaForced, revert, keepCurrent } = useFeatureVersion(featureKey);
  return useMemo(
    () => deriveVersionControls({ canRevert, gaForced, revert, keepCurrent }),
    [canRevert, gaForced, revert, keepCurrent],
  );
}

export interface FeatureGate {
  /** El comercio tiene la funcionalidad (flag de build + treatment/GA). */
  offered: boolean;
  /** Control invitable del rollout + condiciones extra de la feature → etiquetas "Nuevo". */
  inviteEligible: boolean;
  /** inviteEligible + el backend autoriza invitar AHORA (invite_surface, cadencia server-side). */
  showInvite: boolean;
  /** Registrar exhibición de la invitación (dedupe por sesión en el framework). */
  shown: () => void;
  /** Aceptar la invitación: switch optimista a treatment (outbox). */
  accept: (trigger?: string) => void;
  /** "Ahora no": snooze server-side (la cadencia de re-invitación vive en el backend). */
  dismiss: () => void;
  /**
   * Reporta un error de render/runtime de la superficie TREATMENT gateada como
   * behavior='error' (materia prima del guardrail max_error_rate_pct). Fire-and-forget;
   * el hook base ya early-returna fuera de treatment. Para errores de superficies que
   * viven en CONTROL (p.ej. el coachmark de invitación) NO usar esto — van directo a
   * Sentry, no contaminan el error_rate del brazo treatment.
   */
  emitError: (error: unknown, trigger?: string) => void;
  /**
   * Acciones de gestión de versión (volver / quedarme) o `null` si la feature ya no es
   * gestionable (graduó a GA o el backend retiró `can_revert`). Ver el bloque
   * "GESTIÓN DE VERSIÓN" arriba: sin objeto no hay `revert()` que llamar.
   */
  versionControls: FeatureVersionControls | null;
}

export interface FeatureGateConfig {
  // Union tipado (SPEC-8): un slug con typo no compila (sin esto, crearía una feature
  // fantasma con cache/telemetría propios y GETs fallidos silenciosos).
  featureKey: FeatureKey;
  /** trigger_source con el que viajan shown/accept/dismiss (p.ej. 'ble_coachmark'). */
  inviteTrigger: string;
  /** Días de snooze al descartar la invitación. Default 7 (mismo que menu-v2). */
  snoozeDays?: number;
  /**
   * Condiciones EXTRA de invitación de la feature, evaluadas como hook (pueden usar otros
   * hooks: módulos, contexto, capacidades del dispositivo). `true` = este dispositivo/sesión
   * puede ser invitado. Default: sin condiciones extra (siempre invitable si el rollout lo es).
   */
  useInvitable?: () => boolean;
}

// Crea el hook de gate de una feature. Llamar UNA vez a nivel módulo por feature:
//   export const useMiFeatureGate = makeFeatureGateHook({ featureKey: ..., ... });
export function makeFeatureGateHook(config: FeatureGateConfig): () => FeatureGate {
  const { featureKey, inviteTrigger, snoozeDays = 7, useInvitable = () => true } = config;
  return function useFeatureGate(): FeatureGate {
    const fv = useFeatureVersion(featureKey);
    const flagOn = isFeatureBuildEnabled(featureKey);
    const invitable = useInvitable();
    const offered = flagOn && (fv.variant === 'treatment' || fv.gaForced);
    const inviteEligible = flagOn && invitable && fv.isEligibleInvitation;
    return {
      offered,
      inviteEligible,
      showInvite: inviteEligible && fv.inviteSurface !== 'none',
      shown: () => fv.markInvitationShown(inviteTrigger),
      // pushInvite: el accept de una superficie de invitación ES una adopción-push →
      // invitation_accepted se emite estructuralmente para toda feature del contrato (SPEC-1).
      accept: (trigger = inviteTrigger) => fv.switchTo('treatment', trigger, { pushInvite: true }),
      dismiss: () => fv.snooze(snoozeDays, inviteTrigger),
      emitError: (error, trigger) => fv.emitError(error, trigger),
      // Gestión de versión por el MISMO gate para toda feature del contrato: una feature nueva
      // hereda el gate de GA sin re-derivarlo (y sin poder olvidarlo).
      versionControls: deriveVersionControls(fv),
    };
  };
}
